import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import EntityList from "../EntityList/EntityList";
import swal from "sweetalert";
import { defineMessages, useIntl } from "react-intl";
import { usePropertyConfiguration } from "../../../../components/property-configuration-provider";
import { useChainData } from "../../../../components/chain-data-provider";
import { SHARING_TYPE } from "../../Common/constants";
import { isAgrPolicy } from "./domain/agr.helpers";
import { propagateAgrReorder, cloneAgrPolicy, downloadPoliciesExcel } from "./infrastructure/cancellationPolicyAgr.api";
import { createExcelFileDownloadFromRawData } from "../../../../lib/supportFunctions";

const messages = defineMessages({
  conditionsWarning: {
    id: "app.cancellationPolicyList.conditionsWarning",
    defaultMessage: "This cancellation policy is being used in some conditions, it can not be deleted.",
  },
  close: {
    id: "app.cancellationPolicyList.close",
    defaultMessage: "Close",
  },
  disabledDelete: {
    id: "app.cancellationPolicyList.tooltip.delete",
    defaultMessage: "Disabled delete",
  },
  agrOriginTooltip: {
    id: "app.cancellationPolicyList.agrOriginTooltip",
    defaultMessage: "Edit automatic charge settings",
  },
  agrOriginInfo: {
    id: "app.cancellationPolicyList.agrOriginInfo",
    defaultMessage: "This cancellation policy originates from the aggregator engine and can only be managed there.",
  },
  agrReorderBlockedWarning: {
    id: "app.cancellationPolicyList.agrReorderBlockedWarning",
    defaultMessage:
      "The order cannot be changed when the motor only contains aggregator policies and the parameter is disabled. To allow it, go to Settings > Behaviour and enable the parameter «If there are only policies from the aggregator, the order of the individual engine is maintained».",
  },
  agrReorderPropagationError: {
    id: "app.cancellationPolicyList.agrReorderPropagationError",
    defaultMessage:
      "The policy was reordered locally but the change could not be propagated to all child motors. Please reload the page and try again.",
  },
  agrCloneError: {
    id: "app.cancellationPolicyList.agrCloneError",
    defaultMessage: "Could not retrieve the aggregator policy data. The policy was not cloned.",
  },
  motorsMultiple: {
    id: "app.cancellationPolicyList.motorsMultiple",
    defaultMessage: "Multiple",
  },
  usageShared: {
    id: "app.cancellationPolicyList.usageShared",
    defaultMessage: "Shared",
  },
  noDates: {
    id: "app.cancellationPolicyList.noDates",
    defaultMessage: "No dates",
  },
});

function CancellationPolicyList(props) {
  const {
    config,
    success,
    entities,
    loading,
    entityError,
    entityResult,
    handleCreate,
    handleEdit,
    onToggleListItem,
    onDeleteListItem,
    onCloneListItem,
    onReorderListItem,
    hotelTicker,
  } = props;

  const history = useHistory();
  const intl = useIntl();
  const propertyConfig = usePropertyConfiguration();
  const chainData = useChainData();
  const isChain = !!chainData?.isChain;
  const keepAgrPoliciesOrder = propertyConfig?.useChildEngineCancelPolicyOrderInsteadAgr === "1";
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const { name, entityName, validation, fields, renderers, sorting, allowClone, reorder, restConfig } = config;

  const handleToggle = useCallback(
    ({ entity }) => {
      onToggleListItem({ id: entity.id, entity, activeProperty: "active" });
    },
    [onToggleListItem]
  );

  const handleReorder = useCallback(
    ({ id, afterId, ...rest }) => {
      if (!isChain) {
        const allAgrOrigin = entities.length > 0 && entities.every(e => isAgrPolicy(e));
        if (allAgrOrigin && !keepAgrPoliciesOrder) {
          swal({
            text: intl.formatMessage(messages.agrReorderBlockedWarning),
            icon: "warning",
            buttons: { cancel: intl.formatMessage(messages.close) },
          });
          return;
        }
        onReorderListItem({ id, afterId, ...rest });
        return;
      }
      onReorderListItem({ id, afterId, ...rest });

      const baseURL = restConfig?.baseURL || "";
      propagateAgrReorder(baseURL, hotelTicker, id, afterId).catch(e => {
        console.error("AGR reorder propagation failed", e);
        swal({
          text: intl.formatMessage(messages.agrReorderPropagationError),
          icon: "warning",
          buttons: { cancel: intl.formatMessage(messages.close) },
        });
      });
    },
    [isChain, keepAgrPoliciesOrder, entities, hotelTicker, restConfig, onReorderListItem, intl]
  );

  const handleDelete = useCallback(
    ({ event, entity }) => {
      event.preventDefault();
      event.stopPropagation();
      return onDeleteListItem({ id: entity.id, entity });
    },
    [onDeleteListItem]
  );

  const handleClone = useCallback(
    async ({ entity: listEntity, ...rest }) => {
      if (!isChain || !listEntity?.ticker) {
        onCloneListItem({ entity: listEntity, ...rest });
        return;
      }
      try {
        const baseURL = restConfig?.baseURL || "";
        const cloneEntity = await cloneAgrPolicy(baseURL, hotelTicker, listEntity.ticker);
        window.sessionStorage.setItem("duplicate", JSON.stringify(cloneEntity));
        window.sessionStorage.setItem("duplicateEntityName", entityName);
        history.push(`/${hotelTicker}/${entityName}/new`);
      } catch (error) {
        console.error("Error cloning AGR cancellation policy", error);
        swal({
          text: intl.formatMessage(messages.agrCloneError),
          icon: "warning",
          buttons: { cancel: intl.formatMessage(messages.close) },
        });
      }
    },
    [isChain, hotelTicker, entityName, restConfig, onCloneListItem, history, intl]
  );

  const customEntityToListValuesMapper = useCallback(
    ({ entities: entityList, currentLocale }) => {
      return entityList.map(oldEntity => {
        const entity = { ...oldEntity };
        entity._noDatesLabel = intl.formatMessage(messages.noDates);

        const currentTranslation = entity.translations?.find(t => t.locale === currentLocale);
        entity.name = currentTranslation?.name || entity.name || entity.ticker;

        const rawMotors = Array.isArray(entity.motorsList)
          ? entity.motorsList
          : Array.isArray(entity.childPropertyTickers) ? entity.childPropertyTickers : [];

        if (rawMotors.length === 1) {
          entity.motorsList = rawMotors[0];
        } else if (rawMotors.length > 1) {
          entity.motorsList = intl.formatMessage(messages.motorsMultiple);
        } else {
          entity.motorsList = "";
        }

        if (isChain) {
          entity.allowDelete = !entity.linkedToCondition;
          entity.isLinkedToAggregator = true;
        } else {
          const agrOrigin = isAgrPolicy(entity);
          entity.uso = agrOrigin ? intl.formatMessage(messages.usageShared) : "";
          entity.allowDelete = !entity.linkedToCondition && !agrOrigin;
          if (agrOrigin) {
            entity.sharingType = SHARING_TYPE.SHARED;
            entity.allowEdit = false;
          }
        }

        return entity;
      });
    },
    [isChain, intl]
  );

  const filteredFields = useMemo(() => {
    if (!fields || !fields.list) return fields;
    const showAutomaticChargeFields = propertyConfig?.enableAutomaticCharges === "1";
    let listFields;
    if (isChain) {
      listFields = fields.list.filter(f =>
        ["ticker", "name", "motorsList", "dates"].includes(f.fieldName) ||
        (showAutomaticChargeFields && ["enableAutomaticCharge", "penaltyDeposit"].includes(f.fieldName))
      );
    } else {
      listFields = fields.list.filter(f =>
        ["ticker", "dates", "uso"].includes(f.fieldName) ||
        (showAutomaticChargeFields && ["enableAutomaticCharge", "penaltyDeposit"].includes(f.fieldName))
      );
    }
    return { ...fields, list: listFields };
  }, [fields, isChain, propertyConfig]);

  const handleDownloadExcel = useCallback(async () => {
    setIsDownloadingExcel(true);
    try {
      const baseURL = restConfig?.baseURL || "";
      const result = await downloadPoliciesExcel(baseURL, hotelTicker);
      createExcelFileDownloadFromRawData(result.data, result.filename);
    } catch (e) {
      console.error("Error downloading excel", e);
    } finally {
      setIsDownloadingExcel(false);
    }
  }, [hotelTicker, restConfig]);

  const customActions = useMemo(() => {
    const actions = [
      {
        classNames: "btn-danger disabled",
        icon: "fa fa-trash-o",
        tooltipId: "delete-custom",
        tooltipText: intl.formatMessage(messages.disabledDelete),
        onExecute: () => {
          swal({
            text: intl.formatMessage(messages.conditionsWarning),
            icon: "warning",
            buttons: { cancel: intl.formatMessage(messages.close) },
            dangerMode: true,
          });
        },
        isAvailable: ({ entity }) => entity.linkedToCondition && (isChain || !isAgrPolicy(entity)),
      },
    ];
    if (!isChain) {
      actions.push({
        classNames: "",
        icon: "fa fa-pencil",
        tooltipId: "agr-origin-edit",
        tooltipText: intl.formatMessage(messages.agrOriginTooltip),
        onExecute: ({ entity }) => {
          history.push(`/${hotelTicker}/${entityName}/${entity.ticker}`);
        },
        isAvailable: ({ entity }) => isAgrPolicy(entity),
      });
      actions.push({
        classNames: "btn-danger disabled",
        icon: "fa fa-trash-o",
        tooltipId: "delete-agr-origin",
        tooltipText: intl.formatMessage(messages.disabledDelete),
        onExecute: () => {
          swal({
            text: intl.formatMessage(messages.agrOriginInfo),
            icon: "warning",
            buttons: { cancel: intl.formatMessage(messages.close) },
            dangerMode: true,
          });
        },
        isAvailable: ({ entity }) => isAgrPolicy(entity),
      });
    }
    return actions;
  }, [isChain, intl, history, hotelTicker, entityName]);

  return (
    <EntityList
      loading={loading}
      disableSorting
      entityName={entityName}
      headerHelperText={null}
      entities={entities}
      validation={validation}
      renderers={renderers}
      name={name}
      fields={filteredFields}
      onToggle={handleToggle}
      onDelete={handleDelete}
      onClone={handleClone}
      entityError={entityError}
      entityResult={entityResult}
      handleCreate={handleCreate}
      handleEdit={handleEdit}
      sorting={sorting}
      reorder={reorder}
      onReorder={handleReorder}
      success={success}
      allowClone={allowClone}
      customEntityToListValuesMapper={customEntityToListValuesMapper}
      isDownloadingExcel={isDownloadingExcel}
      onDownloadExcel={isChain ? handleDownloadExcel : undefined}
      customActions={customActions}
    />
  );
}

CancellationPolicyList.defaultProps = {
  entities: [],
};

export default CancellationPolicyList;
